<template>
    <ContentTypeShow
        v-if="resource.feature_id == 'pagefeature'"
        :title="resource.name"
        :description="resource.description"
        :can="permissions"
        :modelId="resource.id"
        :mediaIds="resource.mediaIds"
        :availableEmotions="availableEmotions"
    />
    <ShowTemplate
        v-else
        :model="resource"
        :owners="[resource.resourceable].filter((owner) => owner != null)"
        :descriptionTabs="resource.type_id == 'html' ? [] : descriptionTabs"
        :access="resource.access"
        :can="permissions"
        :rolePanels="rolePanels"
        :availableEmotions="availableEmotions"
        :urlLabel="resource.type_id == 'video' ? $lang.get('Video Link') : null"
        :relations="relations"
        :statistics="statistics"
    >
        <template
            #LinkSlot
            v-if="resource.is_linked_resource && resource.linked_resource"
        >
            <OwnerLink
                :modelClass="resource.linked_resource.modelClass"
                :owner="resource.linked_resource"
                :user="resource.linked_resource.user"
                :title="$lang.get('Original')"
                class="mt-4"
            />
        </template>

        <!-- Video slot used if not image is used for the resource -->
        <template #imageSlot v-if="showVideoInImageSlot">
            <YoutubeEmbed
                :url="resource.safeEmbedUrl"
                class="w-full aspect-video"
            />
        </template>

        <template #FirstBodySlot v-if="showHtmlSlot">
            <BlockedIframeWrapper>
                <div v-html="resource.description" />
            </BlockedIframeWrapper>
        </template>

        <template #FirstBodySlot v-else-if="showRssSlot">
            <div>
                <Transition name="fade" mode="out-in">
                    <div
                        :key="feed?.current_page"
                        class="flex flex-col space-y-4"
                    >
                        <FeedItem
                            v-for="item in feed?.data"
                            :key="item.id"
                            :item="item"
                            :class="`border border-border rounded-card p-card`"
                        />
                    </div>
                </Transition>
                <Pagination
                    v-if="feed?.data?.length > 0"
                    @ChangePage="(page) => changePage(page)"
                    :currentPage="feed?.current_page"
                    :perPage="feed?.per_page"
                    :rowsCount="feed?.total"
                    :totalCount="feed?.total"
                    :lastPage="feed?.last_page"
                    class="pt-3"
                />
            </div>
        </template>

        <!-- Video slot used if image is used for the resource -->
        <template #FirstBodySlot v-else-if="useFirstBodySlot">
            <YoutubeEmbed
                v-if="showVideoInFirstBodySlot"
                :url="resource.safeEmbedUrl"
                class="w-full aspect-video"
            />
            <ResourceFile
                v-if="showFileComponent"
                :file="resource.fileDetails"
            />
        </template>

        <!-- List slot used if the resource is a list -->
        <template #SecondBodySlot v-if="resource.type_id == 'list'">
            <ResourceList
                v-if="
                    resource.type_id == 'list' &&
                    resource.formattedListItems.length > 0
                "
                :listItems="resource.formattedListItems"
                :listDesign="resource.data?.design"
            />
        </template>
    </ShowTemplate>
</template>

<script>
import ShowTemplate from '@/Templates/ShowTemplate.vue'
import ContentTypeShow from '@/Pages/Resource/ShowPageVariations/ContentTypeShow.vue'
import ResourceList from '@/Pages/Resource/ShowPageComponents/ResourceList.vue'
import ResourceItem from '@/Components/Panels/RelationPanel/ResourceItem.vue'
import FeedItem from '@/Components/Feed/FeedItem.vue'
import YoutubeEmbed from '@/Components/Elements/YoutubeEmbed.vue'
import ResourceFile from '@/Pages/Resource/ShowPageComponents/ResourceFile.vue'
import OwnerLink from '@/Components/ModelComponents/OwnerLink.vue'
import Pagination from '@/Components/DataTable/Pagination.vue'
import BlockedIframeWrapper from '@/Components/ModelComponents/Resources/BlockedIframeWrapper.vue'
export default {
    components: {
        ShowTemplate,
        ContentTypeShow,
        ResourceList,
        ResourceItem,
        FeedItem,
        YoutubeEmbed,
        ResourceFile,
        OwnerLink,
        Pagination,
        BlockedIframeWrapper,
    },

    props: {
        resource: {
            type: Object,
            required: true,
        },
        permissions: {
            type: Object,
            required: true,
        },
        rolePanels: {
            type: Array,
            required: true,
        },
        availableEmotions: {
            type: Array,
            required: true,
        },
        statistics: {
            type: Object,
            default: {},
        },
        resources: {
            type: Object,
            default: {},
        },
        events: {
            type: Object,
            default: {},
        },
    },

    data() {
        return {
            feed: {},
        }
    },

    mounted() {
        if (this.showRssSlot) {
            this.fetchFeed()
        }
    },

    computed: {
        showHtmlSlot() {
            return this.resource.type_id == 'html'
        },

        showRssSlot() {
            return (
                this.resource.type_id == 'feed' &&
                this.resource.feature_id == 'feedfeature'
            )
        },

        descriptionTabs() {
            return [
                {
                    id: 'description',
                    name: this.$lang.get('Description'),
                    content: this.resource.description,
                },
            ].filter((tab) => !!tab.content)
        },

        showVideoInImageSlot() {
            return (
                this.resource.type_id == 'video' &&
                this.resource.safeEmbedUrl &&
                this.resource.mediaIds.length == 0
            )
        },

        showVideoInFirstBodySlot() {
            return (
                this.resource.type_id == 'video' &&
                this.resource.safeEmbedUrl &&
                this.resource.mediaIds.length > 0
            )
        },

        useFirstBodySlot() {
            return this.showVideoInFirstBodySlot || this.showFileComponent
        },

        showFileComponent() {
            if (
                this.resource.type_id == 'file' &&
                this.resource.file != null &&
                this.resource.fileDetails
            ) {
                let fileExtension = this.resource.file.split('.').pop()
                return [
                    'pdf',
                    'doc',
                    'docx',
                    'xls',
                    'xlsx',
                    'ppt',
                    'pptx',
                ].includes(fileExtension)
            }
        },

        relations() {
            return [this.resources, this.events].filter(
                (relation) => Object.keys(relation).length > 0
            )
        },
    },

    methods: {
        fetchFeed(page = null) {
            let params = {
                resource: this.resource.id,
                limit: 5,
            }
            if (page) {
                params.page = page
            }
            axios
                .get(route('api.resource.feed', params))
                .then((response) => {
                    this.feed = response.data
                })
                .catch((error) => {
                    console.error(error)
                })
        },

        changePage(page) {
            this.fetchFeed(page)
        },
    },
}
</script>
